import { HUD } from "odyssey-shared";
import { barPaged, slotRect } from "../hud/area-slots.ts";
import { HUD_SLOTS, resolveSlot, type HudArea, type SlotRect } from "../hud/slots.ts";
import { ABILITY_SLOT, STATUS_ICON } from "./layout.ts";
import { pageColumn } from "./pages.ts";
import type { TipAnchor } from "./tips.ts";

/**
 * Where the bar's and the effects rows' tooltip stands (P2.2-T12; the design's `abilities` and `status` cards): the
 * design's rich tooltip is 150 wide; a slot's stands `gap` above the bar, its left at the slot's; an icon's stands
 * `below` from its row's top, its left at the icon's; the XP bar's stands `gap` above the bar, `xpLeft` in from its
 * left, where the design's XP card puts its tooltip (P2.4-T8); the stance button's stands `gap` above the button, its
 * left at the button's (P2.2-T13). Each is held inside the HUD's area by its margin.
 */
export const TIP_GEOMETRY = { width: 150, gap: 2, below: 31, xpLeft: 60 } as const;

/**
 * A tooltip's place in logical pixels from the HUD area's top left: its left edge, and either its top (an icon's,
 * hanging down) or its bottom from the area's bottom (a slot's, standing up).
 */
export interface TipPlace {
  readonly left: number;
  readonly top: number | null;
  readonly bottom: number | null;
}

/**
 * Holds a tooltip's left edge inside the area, by the HUD's margin.
 *
 * @param left The left edge wanted.
 * @param area The area.
 * @param width The tooltip's width.
 * @returns The left edge.
 */
function held(left: number, area: HudArea, width: number): number {
  return Math.max(HUD.margin, Math.min(left, area.width - HUD.margin - width));
}

/**
 * Where an anchor's tooltip stands.
 *
 * @param anchor The slot or icon.
 * @param area The HUD's area.
 * @param targetLeft The target's row's left edge, which follows the target frame (`StatusRows`).
 * @param width The tooltip's width: the rich tooltip's, or less for an empty slot's label.
 * @returns The place.
 * @throws RangeError for a slot of the Inventory window, a wallet mark, the carry number, the player card's name or a words wrapper, whose tooltips are placed from their own
 *   measured rectangles (`bag/layout.ts`, `bagTipPlace`; `labelTipPlace`).
 * @example
 * tipPlace({ surface: "slot", index: 1 }, { width: 640, height: 360 }, 144, 150);
 * // { left: 229, top: null, bottom: 28 }
 */
export function tipPlace(anchor: TipAnchor, area: HudArea, targetLeft: number, width: number = TIP_GEOMETRY.width): TipPlace {
  if (anchor.surface === "bag") {
    throw new RangeError("a bag slot's tooltip is placed by bagTipPlace");
  }
  if (anchor.surface === "wallet" || anchor.surface === "carry" || anchor.surface === "name" || anchor.surface === "words") {
    throw new RangeError("a wallet mark's, the carry number's, the player card name's or a words wrapper's tooltip is placed by labelTipPlace");
  }
  if (anchor.surface === "xp") {
    const xp = slotRect("xp", area);
    return { left: held(xp.left + TIP_GEOMETRY.xpLeft, area, width), top: null, bottom: area.height - xp.top + TIP_GEOMETRY.gap };
  }
  if (anchor.surface === "stance") {
    const button = slotRect("stance", area);
    return { left: held(button.left, area, width), top: null, bottom: area.height - button.top + TIP_GEOMETRY.gap };
  }
  if (anchor.surface === "slot") {
    const bar = slotRect("abilities", area);
    const column = barPaged(area) ? pageColumn(anchor.index) : anchor.index;
    return { left: held(bar.left + column * (ABILITY_SLOT.size + ABILITY_SLOT.gap), area, width), top: null, bottom: area.height - bar.top + TIP_GEOMETRY.gap };
  }
  const row = resolveSlot(anchor.surface === "own" ? HUD_SLOTS.status : HUD_SLOTS.targetStatus, area);
  const left = (anchor.surface === "own" ? row.left : targetLeft) + anchor.index * (STATUS_ICON.column + STATUS_ICON.gap);
  return { left: held(left, area, width), top: row.top + TIP_GEOMETRY.below, bottom: null };
}

/**
 * Where a label tooltip of a measured mark stands (a wallet mark's, P2.8-T67): `gap` above the mark, its left at the
 * mark's, held inside the area by its margin; `gap` below the mark when there is no room above.
 *
 * @param mark The mark's rectangle in logical pixels of the area.
 * @param area The HUD's area.
 * @param width The tooltip's laid-out width.
 * @param height The tooltip's laid-out height.
 * @returns The place.
 * @example
 * labelTipPlace({ left: 400, top: 300, width: 30, height: 16 }, { width: 640, height: 360 }, 60, 14);
 * // { left: 400, top: null, bottom: 62 }
 */
export function labelTipPlace(mark: SlotRect, area: HudArea, width: number, height: number): TipPlace {
  const left = held(mark.left, area, width);
  if (mark.top - TIP_GEOMETRY.gap - height < HUD.margin) {
    return { left, top: mark.top + mark.height + TIP_GEOMETRY.gap, bottom: null };
  }
  return { left, top: null, bottom: area.height - mark.top + TIP_GEOMETRY.gap };
}
